import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  WorkflowNode,
  TriggerCondition,
  Approver,
  DelegationUser,
} from '../../shared-services/pipeline-workflow.model';

type PipelineNodeCardWidgetData = {
  node?: WorkflowNode;
  index?: number;
  config?: any;
  onUpdateNode?: (node: WorkflowNode) => void;
  onDeleteNode?: (nodeId: string) => void;
  onDuplicateNode?: (nodeId: string) => void;
  onTestNode?: (nodeId: string) => void;
};

@Component({
  selector: 'app-pipeline-node-card',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './pipeline-node-card.component.html',
  styleUrl: './pipeline-node-card.component.scss',
})
export class PipelineNodeCardComponent {
  @Input() node!: WorkflowNode;
  @Input() index: number = 0;
  @Input() config: any = null;
  @Input() set data(value: PipelineNodeCardWidgetData | null | undefined) {
    if (!value) return;
    if (value.node) this.node = value.node;
    this.index = value.index ?? this.index;
    this.config = value.config ?? this.config;
    this.onUpdateNode = value.onUpdateNode;
    this.onDeleteNode = value.onDeleteNode;
    this.onDuplicateNode = value.onDuplicateNode;
    this.onTestNode = value.onTestNode;
  }

  @Output() updateNode    = new EventEmitter<WorkflowNode>();
  @Output() deleteNode    = new EventEmitter<string>();
  @Output() duplicateNode = new EventEmitter<string>();
  @Output() testNode      = new EventEmitter<string>();

  hoveredPanel: string | null = null;
  hoveredApprover: number | null = null;
  hoveredTrigger: number | null = null;
  hoveredDelegation: number | null = null;
  showApproverMenu = false;
  showDelegationMenu = false;

  readonly defaultOperators = ['>', '<', '>=', '<=', '=', '!='];
  private onUpdateNode?: (node: WorkflowNode) => void;
  private onDeleteNode?: (nodeId: string) => void;
  private onDuplicateNode?: (nodeId: string) => void;
  private onTestNode?: (nodeId: string) => void;

  get operators(): string[] {
    const configured = this.config?.panels?.triggers?.operatorOptions;
    return Array.isArray(configured) && configured.length
      ? configured.map((value) => String(value || '').trim()).filter(Boolean)
      : this.defaultOperators;
  }

  get quickApproverOptions(): any[] {
    const configured = this.config?.panels?.approvers?.quickAddOptions;
    if (!Array.isArray(configured) || !configured.length) {
      return [
        { name: 'Team Lead', type: 'role', memberCount: 1, avatarText: 'TL' },
        { name: 'UI/UX Team', type: 'role', memberCount: 3, avatarText: 'UX', avatarTone: 'primary' },
        { name: 'Engineering Manager', type: 'role', memberCount: 1, avatarText: 'EM', avatarTone: 'muted' },
      ];
    }

    return configured;
  }

  get quickDelegationOptions(): any[] {
    const configured = this.config?.panels?.delegation?.quickAddOptions;
    if (!Array.isArray(configured) || !configured.length) {
      return [
        { name: 'Backup Finance Team', type: 'role', memberCount: 2, avatarText: 'BF', avatarTone: 'muted' },
        { name: 'Ops Director', type: 'user', description: 'Escalation manager', avatarText: 'OD', avatarTone: 'primary' },
      ];
    }

    return configured;
  }

  get visiblePanels(): string[] {
    return ['triggers', 'approvers', 'decisionRule', 'delegation', 'sla', 'metadata']
      .filter((panel) => this.isPanelVisible(panel));
  }

  get showHeader(): boolean {
    return this.readFlag(this.config?.header?.visible, true);
  }

  get showFlowContext(): boolean {
    return this.readFlag(this.config?.flowContext?.visible, true);
  }

  get showHeaderIndex(): boolean {
    return this.readFlag(this.config?.header?.showIndex, true);
  }

  get showHeaderTypeBadge(): boolean {
    return this.readFlag(this.config?.header?.showTypeBadge, true);
  }

  get showHeaderLabel(): boolean {
    return this.readFlag(this.config?.header?.showLabel, true);
  }

  get showHeaderActions(): boolean {
    return this.readFlag(this.config?.header?.showActions, true);
  }

  get showTestAction(): boolean {
    return this.readFlag(this.config?.header?.actions?.test?.visible, true);
  }

  get showDuplicateAction(): boolean {
    return this.readFlag(this.config?.header?.actions?.duplicate?.visible, true);
  }

  get showDeleteAction(): boolean {
    return this.readFlag(this.config?.header?.actions?.delete?.visible, true);
  }

  get gateLabelPrefix(): string {
    return this.readText(this.config?.flowContext?.gateLabelPrefix, 'Gate');
  }

  get flowContextSeparator(): string {
    return this.readText(this.config?.flowContext?.separator, '-');
  }

  get triggerPanelTitle(): string {
    return this.readText(this.config?.panels?.triggers?.title, 'TRIGGER CONDITIONS');
  }

  get approverPanelTitle(): string {
    return this.readText(this.config?.panels?.approvers?.title, 'APPROVERS');
  }

  get decisionRulePanelTitle(): string {
    return this.readText(this.config?.panels?.decisionRule?.title, 'DECISION RULE');
  }

  get delegationPanelTitle(): string {
    return this.readText(this.config?.panels?.delegation?.title, 'DELEGATION');
  }

  get slaPanelTitle(): string {
    return this.readText(this.config?.panels?.sla?.title, 'SLA & ESCALATION');
  }

  get metadataPanelTitle(): string {
    return this.readText(this.config?.panels?.metadata?.title, 'METADATA');
  }

  getBadgeClass(): Record<string, boolean> {
    return {
      'badge-auto':      this.node.type === 'AUTO_APPROVE',
      'badge-parallel':  this.node.type === 'PARALLEL',
      'badge-hierarchy': this.node.type === 'HIERARCHY',
    };
  }

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }

  addCondition(): void {
    if (!this.readFlag(this.config?.panels?.triggers?.allowAdd, true)) {
      return;
    }
    const newCondition: TriggerCondition = {
      id: `tc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      field: '', operator: '>', value: '', currency: 'USD',
    };
    const updated = { ...this.node, triggerConditions: [...this.node.triggerConditions, newCondition] };
    this.node = updated;
    this.emitUpdatedNode(updated);
  }

  removeTrigger(i: number): void {
    if (!this.readFlag(this.config?.panels?.triggers?.allowRemove, true)) {
      return;
    }
    const conditions = [...this.node.triggerConditions];
    conditions.splice(i, 1);
    const updated = { ...this.node, triggerConditions: conditions };
    this.node = updated;
    this.emitUpdatedNode(updated);
  }

  updateTrigger(id: string, field: string, event: Event): void {
    const val = (event.target as HTMLInputElement | HTMLSelectElement).value;
    const conditions = this.node.triggerConditions.map((tc) =>
      tc.id !== id ? tc : { ...tc, [field]: field === 'value' && val !== '' ? Number(val) : val }
    );
    const updated = { ...this.node, triggerConditions: conditions };
    this.node = updated;
    this.emitUpdatedNode(updated);
  }

  removeApprover(i: number): void {
    if (!this.readFlag(this.config?.panels?.approvers?.allowRemove, true)) {
      return;
    }
    const approvers = [...this.node.approvers];
    approvers.splice(i, 1);
    const updated = { ...this.node, approvers };
    this.node = updated;
    this.emitUpdatedNode(updated);
  }

  addPredefinedApprover(name: string, type: 'user' | 'role', memberCount?: number): void {
    if (!this.readFlag(this.config?.panels?.approvers?.allowQuickAdd, true)) {
      return;
    }
    const newApprover: Approver = {
      id: `apr-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, name, type,
      ...(memberCount !== undefined ? { memberCount } : {}),
    };
    const updated = { ...this.node, approvers: [...this.node.approvers, newApprover] };
    this.node = updated;
    this.showApproverMenu = false;
    this.emitUpdatedNode(updated);
  }

  removeDelegate(i: number): void {
    if (!this.readFlag(this.config?.panels?.delegation?.allowRemove, true)) {
      return;
    }
    const delegationUsers = [...(this.node.delegationUsers || [])];
    delegationUsers.splice(i, 1);
    const updated = { ...this.node, delegationUsers };
    this.node = updated;
    this.emitUpdatedNode(updated);
  }

  addPredefinedDelegation(name: string, type: 'user' | 'role', memberCount?: number, desc?: string): void {
    if (!this.readFlag(this.config?.panels?.delegation?.allowQuickAdd, true)) {
      return;
    }
    const newDelegate: DelegationUser = {
      id: `del-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, name, type,
      ...(memberCount !== undefined ? { memberCount } : {}),
      ...(desc ? { description: desc } : {}),
    };
    const updated = { ...this.node, delegationUsers: [...(this.node.delegationUsers || []), newDelegate] };
    this.node = updated;
    this.showDelegationMenu = false;
    this.emitUpdatedNode(updated);
  }

  updateDecisionRule(field: string, event: Event): void {
    const val = (event.target as HTMLInputElement | HTMLSelectElement).value;
    const updated = {
      ...this.node,
      decisionRule: {
        ...this.node.decisionRule,
        [field]: (field === 'minApprovals' || field === 'totalApprovers') ? Number(val) : val,
      },
    };
    this.node = updated;
    this.emitUpdatedNode(updated);
  }

  updateSLA(field: string, event: Event): void {
    const val = (event.target as HTMLInputElement | HTMLSelectElement).value;
    const numericFields = ['reminderHours', 'maxEscalations', 'worstCaseHours'];
    const updated = {
      ...this.node,
      sla: { ...this.node.sla, [field]: numericFields.includes(field) ? Number(val) : val },
    };
    this.node = updated;
    this.emitUpdatedNode(updated);
  }

  handleDeleteNode(): void {
    if (!this.showDeleteAction) {
      return;
    }
    this.deleteNode.emit(this.node.id);
    this.onDeleteNode?.(this.node.id);
  }

  handleDuplicateNode(): void {
    if (!this.showDuplicateAction) {
      return;
    }
    this.duplicateNode.emit(this.node.id);
    this.onDuplicateNode?.(this.node.id);
  }

  handleTestNode(): void {
    if (!this.showTestAction) {
      return;
    }
    this.testNode.emit(this.node.id);
    this.onTestNode?.(this.node.id);
  }

  isPanelVisible(panelKey: string): boolean {
    return this.readFlag(this.config?.panels?.[panelKey]?.visible, true);
  }

  showPanelCount(panelKey: string): boolean {
    return this.readFlag(this.config?.panels?.[panelKey]?.showCount, true);
  }

  showField(panelKey: string, fieldKey: string, fallback = true): boolean {
    return this.readFlag(this.config?.panels?.[panelKey]?.fields?.[fieldKey]?.visible, fallback);
  }

  isEditable(panelKey: string, fieldKey: string, fallback = true): boolean {
    return this.readFlag(this.config?.panels?.[panelKey]?.fields?.[fieldKey]?.editable, fallback);
  }

  actionLabel(panelKey: string, actionKey: string, fallback: string): string {
    return this.readText(this.config?.panels?.[panelKey]?.actions?.[actionKey]?.label, fallback);
  }

  headerActionLabel(actionKey: string, fallback: string): string {
    return this.readText(this.config?.header?.actions?.[actionKey]?.label, fallback);
  }

  metadataLabel(fieldKey: string, fallback: string): string {
    return this.readText(this.config?.panels?.metadata?.labels?.[fieldKey], fallback);
  }

  slaLabel(fieldKey: string, fallback: string): string {
    return this.readText(this.config?.panels?.sla?.labels?.[fieldKey], fallback);
  }

  delegationSupported(): boolean {
    const supported = this.config?.panels?.delegation?.supportedNodeTypes;
    if (Array.isArray(supported) && supported.length) {
      return supported.includes(this.node.type);
    }

    return this.node.type === 'PARALLEL';
  }

  decisionRuleSummary(): string {
    const labels = this.config?.flowContext?.typeDescriptions || {};
    return this.node.type === 'AUTO_APPROVE'
      ? this.readText(labels?.AUTO_APPROVE, 'Auto-route - no human needed')
      : this.node.type === 'PARALLEL'
        ? this.readText(labels?.PARALLEL, 'Parallel review - all at once')
        : this.readText(labels?.HIERARCHY, 'Hierarchy chain - sequential');
  }

  quickOptionAvatar(option: any): string {
    return this.readText(option?.avatarText, this.getInitials(String(option?.name || '')));
  }

  quickOptionToneClass(option: any): string {
    const tone = String(option?.avatarTone || '').trim().toLowerCase();
    return tone === 'primary' ? 'role-icon-primary' : tone === 'muted' ? 'role-icon-muted' : '';
  }

  quickOptionMeta(option: any, fallbackPrefix: string): string {
    if (option?.description) {
      return String(option.description);
    }

    const type = String(option?.type || fallbackPrefix).toUpperCase();
    const memberCount = Number(option?.memberCount);
    if (Number.isFinite(memberCount) && memberCount > 0) {
      return `${type} - ${memberCount} member${memberCount > 1 ? 's' : ''}`;
    }

    return type;
  }

  timeoutOptions(): string[] {
    const configured = this.config?.panels?.sla?.timeoutOptions;
    if (Array.isArray(configured) && configured.length) {
      return configured.map((value) => String(value || '').trim()).filter(Boolean);
    }

    return ['ESCALATE', 'AUTO_APPROVE', 'REJECT'];
  }

  inputPlaceholder(panelKey: string, fieldKey: string, fallback = ''): string {
    return this.readText(this.config?.panels?.[panelKey]?.fields?.[fieldKey]?.placeholder, fallback);
  }

  private emitUpdatedNode(updated: WorkflowNode): void {
    this.updateNode.emit(updated);
    this.onUpdateNode?.(updated);
  }

  readFlag(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
  }

  readText(value: unknown, fallback: string): string {
    return typeof value === 'string' && value.trim() ? value.trim() : fallback;
  }
}
